import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import changesetSet from 'ember-changeset/helpers/changeset-set';
import add from 'ember-math-helpers/helpers/add';
import { eq } from 'ember-truth-helpers';
import WrButton from 'ember-wiser/components/button';
import WrDetails from 'ember-wiser/components/details';
import WrForm from 'ember-wiser/components/form';
import FileAttachementRow from 'parog-web/components/dialysis/edition-form/file-attachement-row';
import type DpMeasuredJournalEvent from 'parog-web/models/dp-measured-journal-event';
import type TypedEmberChangeset from 'parog-web/utils/typed-ember-changeset';
import Dropdown from 'ui-kit/components/dropdown';

interface Signature {
  Args: {
    measurements: TypedEmberChangeset<DpMeasuredJournalEvent>[];
    onSubmit: () => Promise<void>;
  };
}

interface MiddleSessionSignature {
  Args: {
    measurement: TypedEmberChangeset<DpMeasuredJournalEvent>;
    onSubmit: () => Promise<void>;
  };
}

class MiddleSession extends Component<MiddleSessionSignature> {
  get changesetErrors() {
    const occurredAtError = this.args.measurement.errors.find(
      e => e.key === 'occurredAtChronology'
    );

    return occurredAtError?.validation as string;
  }

  get cycleUltrafiltrationVolume() {
    const cyclePocketInfusedVolume = this.args.measurement.get(
      'cyclePocketInfusedVolume'
    );
    const cyclePocketDrainedVolume = this.args.measurement.get(
      'cyclePocketDrainedVolume'
    );

    if (!cyclePocketInfusedVolume || !cyclePocketDrainedVolume) return '-';

    return cyclePocketDrainedVolume - cyclePocketInfusedVolume;
  }

  clearOccurredAtError = () => {
    this.args.measurement.removeError('occurredAtChronology');
  };

  <template>
    <WrForm @data={{@measurement}} @onSubmit={{@onSubmit}} as |form|>
      <form.InputRow
        @name='occurredAt'
        @label='Date et heure *'
        type='datetime-local'
        required={{true}}
        class={{if this.changesetErrors 'text-red border-b-red'}}
        {{on 'input' this.clearOccurredAtError}}
      >
        {{#if this.changesetErrors}}
          <div
            class='text-red pt-2 leading-[1.094rem] sm:pt-0'
            aria-label='occurredAtChronologyError'
          >{{this.changesetErrors}}
          </div>
        {{/if}}
      </form.InputRow>

      <form.InputRow
        @label='Volume de la poche infusée'
        @name='cyclePocketInfusedVolume'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 4'
      >L</form.InputRow>

      <form.InputRow
        @label='Volume de la poche drainée'
        @name='cyclePocketDrainedVolume'
        min='0'
        type='number'
        class='mb-2 sm:mb-0'
        placeholder='Ex: 6'
      >L</form.InputRow>

      <form.Row aria-label='cycleUltrafiltrationVolume'>
        <:header> Volume d’ultrafiltration</:header>
        <:col1>
          {{this.cycleUltrafiltrationVolume}}
        </:col1>
        <:col2>
          <div class='mb-2 sm:mb-0'> L</div>
        </:col2>
      </form.Row>

      <form.Row aria-label='cyclePocketAspect'>
        <:header>Aspect de la poche</:header>
        <:col1>
          <Dropdown
            @selected={{changesetGet @measurement 'cyclePocketAspect'}}
            @onChange={{changesetSet @measurement 'cyclePocketAspect'}}
            class='mt-2 sm:mt-0'
            as |dropdown|
          >
            {{#each cyclePocketAspectOptions as |option|}}
              <dropdown.Item @key={{option}} @value={{option}} />
            {{/each}}
          </Dropdown>
        </:col1>
      </form.Row>

      <FileAttachementRow
        @form={{form}}
        @title='Prendre une photo de la poche'
      />

      <form.TextareaRow
        @label='Commentaires patient'
        @name='comments'
        class='mt-2 sm:mt-0'
        placeholder='Laisser un commentaire…'
      >
        &nbsp;
      </form.TextareaRow>

      {{! Hack: add a hidden native submit button so that
          pressing "Enter" triggers a proper HTML form submission. }}
      <button type='submit' class='hidden'></button>

    </WrForm>
  </template>
}

export default class CycleSessionEditionForm extends Component<Signature> {
  @tracked cycleNumber =
    this.args.measurements.filter(m => !m.isNew).length ||
    this.args.measurements.filter(m => m.isDirty).length ||
    1;

  get activeCycles() {
    return this.args.measurements.slice(0, this.cycleNumber);
  }

  addCycle = () => {
    this.cycleNumber += 1;
  };
  <template>
    {{#each this.activeCycles as |measurement index|}}
      <WrDetails @summary={{concat 'Cycle ' (add index 1)}} class='mt-2'>
        <MiddleSession @measurement={{measurement}} @onSubmit={{@onSubmit}} />

      </WrDetails>
    {{/each}}

    <div
      class='fixed bottom-[4rem] left-0 right-0 bg-[#f7f7f7] px-2 pt-2 sm:relative sm:top-[0.25em] sm:mx-0 sm:w-full sm:bg-transparent sm:p-0'
    >
      <WrButton
        {{on 'click' this.addCycle}}
        disabled={{eq this.cycleNumber 20}}
        @variant='outlined'
        class='mr-1 w-full'
        type='button'
      >
        Démarrer un nouveau cycle
      </WrButton>
    </div>
  </template>
}

export const cyclePocketAspectOptions = [
  'Clair',
  'Trouble',
  'Rosé',
  'Présence des filaments blanchâtres (fibrineux)'
] as const;
